import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import MaintenanceBanner from "@/components/MaintenanceBanner";
import CookieConsent from "@/components/CookieConsent";
import LandingPage from "@/pages/LandingPage";
import FullAuditSetup from "@/pages/FullAuditSetup";
import RunningPage from "@/pages/RunningPage";
import ReportDashboard from "@/pages/ReportDashboard";
import SampleReport from "@/pages/SampleReport";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminPage from "@/pages/AdminPage";
import DashboardPage from "@/pages/DashboardPage";
import PricingPage from "@/pages/PricingPage";
import TermsPage from "@/pages/TermsPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import FAQPage from "@/pages/FAQPage";
import AboutPage from "@/pages/AboutPage";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Private paths excluded from Google Analytics
const PRIVATE_PATHS = ['/dashboard', '/admin', '/report/', '/audit/', '/full-audit', '/login', '/reset-password', '/baseline'];

function useGoogleAnalytics() {
  const location = useLocation();
  
  useEffect(() => {
    const isPrivatePage = PRIVATE_PATHS.some(path => 
      location.pathname === path || location.pathname.startsWith(path)
    );
    
    // Only send pageview for public pages
    if (!isPrivatePage && window.gtag) {
      window.gtag('config', 'G-N5ZW9KPDCM', {
        page_path: location.pathname + location.search
      });
    }
  }, [location]);
}

function AppRouter() {
  const location = useLocation();
  
  // Track page views (only for public pages)
  useGoogleAnalytics();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Each page now manages its own navigation:
  // - Marketing pages (/, /pricing, /faq, /about, /terms, /sample-report) use GlobalNav
  // - App pages (/dashboard, /full-audit, /audit/:id, /report/:id, /admin) use AppLayout with sidebar
  // - Auth pages (/login, /reset-password) have standalone layout

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/audit" element={<Navigate to="/full-audit" replace />} />
      <Route path="/baseline" element={<Navigate to="/full-audit" replace />} />
      <Route path="/full-audit" element={<FullAuditSetup />} />
      <Route path="/audit/:id" element={<RunningPage />} />
      <Route path="/report/:id" element={<ReportDashboard />} />
      <Route path="/sample-report" element={<SampleReport />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="min-h-screen bg-[#0A0A0A]">
          <BrowserRouter>
            <ScrollToTop />
            <MaintenanceBanner />
            <AppRouter />
          </BrowserRouter>
          <Toaster theme="dark" />
          <CookieConsent />
        </div>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
